import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Users, Settings, UserPlus, Loader2 } from 'lucide-react'
import { useAuth, type UserRole } from '@/contexts/AuthContext'
import { useOrganizationMembersQuery } from '@/data/teamMembers'

const roleLabels: Record<UserRole, string> = {
  super_admin: 'Super Admin',
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  viewer: 'Viewer',
}

const roleBadgeColors: Record<UserRole, string> = {
  super_admin: 'bg-destructive/10 text-destructive',
  owner: 'bg-violet-500/10 text-violet-500',
  admin: 'bg-blue-500/10 text-blue-500',
  manager: 'bg-emerald-500/10 text-emerald-500',
  viewer: 'bg-muted text-muted-foreground',
}

export default function TeamPage() {
  const { organization, hasPermission } = useAuth()
  const canManageTeam = hasPermission('manage_team')
  const isBusinessTier = organization?.tier === 'business'
  const { data: teamMembers = [], isLoading } = useOrganizationMembersQuery(organization?.id)

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Team</h1>
          <p className="text-muted-foreground">
            {canManageTeam ? 'Manage your team members' : 'View your team members'}
          </p>
        </div>
        {canManageTeam && (
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to="/team/manage">
                <Settings className="mr-2 h-4 w-4" />
                Manage Team
              </Link>
            </Button>
            <Button asChild>
              <Link to="/team/invite">
                <UserPlus className="mr-2 h-4 w-4" />
                Invite Member
              </Link>
            </Button>
          </div>
        )}
      </div>

      {!canManageTeam && isBusinessTier && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
          <p className="text-sm text-muted-foreground">
            You have view-only access to the team list. Contact your team admin to manage members.
          </p>
        </div>
      )}

      {isBusinessTier ? (
        <div className="bg-card text-card-foreground rounded-lg border">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="font-semibold">Team Members</h2>
            <span className="text-sm text-muted-foreground">
              {isLoading ? '…' : `${teamMembers.length} members`}
            </span>
          </div>
          <div className="divide-y">
            {isLoading ? (
              <div className="p-8 flex justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : teamMembers.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No team members yet.
              </div>
            ) : (
              teamMembers.map((member) => {
                const displayName = member.user.name ?? member.user.email.split('@')[0]
                return (
                  <div key={member.id} className="p-4 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium overflow-hidden">
                        {member.user.avatar_url ? (
                          <img src={member.user.avatar_url} alt="" className="h-full w-full object-cover" />
                        ) : (
                          displayName.charAt(0).toUpperCase()
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{displayName}</p>
                        <p className="text-sm text-muted-foreground">{member.user.email}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleBadgeColors[member.role]}`}>
                      {roleLabels[member.role]}
                    </span>
                  </div>
                )
              })
            )}
          </div>
        </div>
      ) : (
        /* Empty State for non-Business users */
        <div className="bg-card text-card-foreground rounded-lg border p-12 text-center">
          <Users className="h-12 w-12 text-muted-foreground mx-auto" />
          <h3 className="mt-4 text-lg font-semibold">Team Management</h3>
          <p className="mt-2 text-muted-foreground">
            Team management is available on Business plans.
            <br />
            Invite colleagues to help manage your drops.
          </p>
        </div>
      )}

      {!isBusinessTier && (
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg p-6 border border-primary/20">
          <h3 className="font-semibold text-primary">Upgrade to Business</h3>
          <p className="text-sm text-muted-foreground mt-2">
            Team management is available on Business plans. Get 10 team members included,
            with role-based permissions for owners, admins, managers, and viewers.
          </p>
          <Button className="mt-4" asChild>
            <Link to="/settings/billing">View Business Plan</Link>
          </Button>
        </div>
      )}
    </div>
  )
}
